import { usePageTitle } from '../hooks/usePageTitle';
import React, { useState, useEffect } from 'react';
import { aiAPI, clientAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles,
  FileText,
  BookOpen,
  GraduationCap,
  Loader2,
  Download,
  Copy,
  CheckCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { todayIST } from '../lib/utils';

const DEPARTMENTS = [
  'Emergency',
  'OPD',
  'IPD',
  'ICU',
  'Operation Theatre',
  'Laboratory',
  'Radiology',
  'Pharmacy',
  'Nursing',
  'Administration',
  'Infection Control',
  'Quality',
  'Housekeeping',
  'Dietary',
  'CSSD',
];

const ORGANIZATION_TYPES = [
  'Hospital',
  'Nursing Home',
  'Diagnostic Center',
  'Laboratory',
  'Day Care Center',
];

export const AIToolsPage = () => {
  const { isAdmin } = useAuth();
  usePageTitle('AI Tools');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [copied, setCopied] = useState(false);

  // Gap Report Form
  const [gapReportForm, setGapReportForm] = useState({
    client_id: '',
    areas_reviewed: 'Patient Care, Safety, Infrastructure, Documentation, Quality Indicators',
    observations: '',
  });

  // SOP Form
  const [sopForm, setSopForm] = useState({
    department: '',
    process_name: '',
    organization_type: 'Hospital',
    additional_context: '',
  });

  // Training Module Form
  const [trainingForm, setTrainingForm] = useState({
    topic: '',
    target_audience: '',
    duration_hours: 2,
    objectives: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const handleGenerateGapReport = async (e) => {
    e.preventDefault();
    if (!gapReportForm.client_id) {
      toast.error('Please select a client');
      return;
    }

    setLoading(true);
    setGeneratedContent('');
    try {
      const response = await aiAPI.generateGapReport(gapReportForm);
      setGeneratedContent(response.data.report);
      toast.success('Gap Analysis Report generated and saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSOP = async (e) => {
    e.preventDefault();
    if (!sopForm.department || !sopForm.process_name) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    setGeneratedContent('');
    try {
      const response = await aiAPI.generateSOP(sopForm);
      setGeneratedContent(response.data.sop);
      toast.success('SOP document generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate SOP');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTrainingModule = async (e) => {
    e.preventDefault();
    if (!trainingForm.topic || !trainingForm.target_audience) {
      toast.error('Please fill in required fields');
      return;
    }

    setLoading(true);
    setGeneratedContent('');
    try {
      const objectives = trainingForm.objectives
        ? trainingForm.objectives.split('\n').filter(Boolean)
        : [];
      const response = await aiAPI.generateTrainingModule({
        ...trainingForm,
        objectives,
      });
      setGeneratedContent(response.data.training_module);
      toast.success('Training Module generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate training module');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([generatedContent], { type: 'text/markdown' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `generated_document_${todayIST()}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success('Document downloaded!');
  };

  return (
    <div className="space-y-6 animate-in" data-testid="ai-tools-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-400 p-6 text-white">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%)'}} />
        <div className="relative z-10 flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold">AI Document Generator</h1>
            <p className="text-purple-100">
              Generate NABH-compliant documents powered by AI
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Generation Forms */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Generate Documents</CardTitle>
            <CardDescription>Select a document type and provide details</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="gap-report" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="gap-report" className="text-xs sm:text-sm">
                  <FileText className="w-4 h-4 mr-1 hidden sm:inline" />
                  Gap Report
                </TabsTrigger>
                <TabsTrigger value="sop" className="text-xs sm:text-sm">
                  <BookOpen className="w-4 h-4 mr-1 hidden sm:inline" />
                  SOP
                </TabsTrigger>
                <TabsTrigger value="training" className="text-xs sm:text-sm">
                  <GraduationCap className="w-4 h-4 mr-1 hidden sm:inline" />
                  Training
                </TabsTrigger>
              </TabsList>

              {/* Gap Report Tab */}
              <TabsContent value="gap-report" className="mt-4">
                <form onSubmit={handleGenerateGapReport} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="client">Client *</Label>
                    <Select
                      value={gapReportForm.client_id}
                      onValueChange={(value) =>
                        setGapReportForm({ ...gapReportForm, client_id: value })
                      }
                    >
                      <SelectTrigger data-testid="gap-client-select">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent>
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.organization_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="areas">Areas Reviewed</Label>
                    <Input
                      id="areas"
                      value={gapReportForm.areas_reviewed}
                      onChange={(e) =>
                        setGapReportForm({ ...gapReportForm, areas_reviewed: e.target.value })
                      }
                      placeholder="Patient Care, Safety, Infrastructure..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="observations">Initial Observations</Label>
                    <Textarea
                      id="observations"
                      value={gapReportForm.observations}
                      onChange={(e) =>
                        setGapReportForm({ ...gapReportForm, observations: e.target.value })
                      }
                      rows={3}
                      placeholder="Describe key observations from the initial visit..."
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Gap Report
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* SOP Tab */}
              <TabsContent value="sop" className="mt-4">
                <form onSubmit={handleGenerateSOP} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="department">Department *</Label>
                    <Select
                      value={sopForm.department}
                      onValueChange={(value) => setSopForm({ ...sopForm, department: value })}
                    >
                      <SelectTrigger data-testid="sop-dept-select">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map((dept) => (
                          <SelectItem key={dept} value={dept}>
                            {dept}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="process">Process/Procedure Name *</Label>
                    <Input
                      id="process"
                      value={sopForm.process_name}
                      onChange={(e) => setSopForm({ ...sopForm, process_name: e.target.value })}
                      placeholder="e.g., Patient Admission Process"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="org-type">Organization Type</Label>
                    <Select
                      value={sopForm.organization_type}
                      onValueChange={(value) =>
                        setSopForm({ ...sopForm, organization_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ORGANIZATION_TYPES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="context">Additional Context</Label>
                    <Textarea
                      id="context"
                      value={sopForm.additional_context}
                      onChange={(e) =>
                        setSopForm({ ...sopForm, additional_context: e.target.value })
                      }
                      rows={2}
                      placeholder="Any specific requirements or constraints..."
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate SOP
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Training Module Tab */}
              <TabsContent value="training" className="mt-4">
                <form onSubmit={handleGenerateTrainingModule} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="topic">Training Topic *</Label>
                    <Input
                      id="topic"
                      value={trainingForm.topic}
                      onChange={(e) => setTrainingForm({ ...trainingForm, topic: e.target.value })}
                      placeholder="e.g., Infection Control Practices"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="audience">Target Audience *</Label>
                    <Input
                      id="audience"
                      value={trainingForm.target_audience}
                      onChange={(e) =>
                        setTrainingForm({ ...trainingForm, target_audience: e.target.value })
                      }
                      placeholder="e.g., Nursing Staff, All Clinical Staff"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (hours)</Label>
                    <Input
                      id="duration"
                      type="number"
                      min="1"
                      max="8"
                      value={trainingForm.duration_hours}
                      onChange={(e) =>
                        setTrainingForm({
                          ...trainingForm,
                          duration_hours: parseInt(e.target.value) || 2,
                        })
                      }
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="objectives">Learning Objectives (one per line)</Label>
                    <Textarea
                      id="objectives"
                      value={trainingForm.objectives}
                      onChange={(e) =>
                        setTrainingForm({ ...trainingForm, objectives: e.target.value })
                      }
                      rows={3}
                      placeholder="Understand hand hygiene protocols&#10;Demonstrate proper PPE usage&#10;Identify infection risks"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Training Module
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Generated Content Preview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Generated Document</CardTitle>
                <CardDescription>Preview and download your document</CardDescription>
              </div>
              {generatedContent && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                <Loader2 className="w-12 h-12 animate-spin mb-4" />
                <p>Generating document with AI...</p>
                <p className="text-sm">This may take a few moments</p>
              </div>
            ) : generatedContent ? (
              <ScrollArea className="h-96 border rounded-lg p-4">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap text-sm font-mono bg-slate-50 p-4 rounded-lg">
                    {generatedContent}
                  </pre>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex flex-col items-center justify-center h-96 text-muted-foreground">
                <Sparkles className="w-12 h-12 mb-4 opacity-30" />
                <p>Your generated document will appear here</p>
                <p className="text-sm">Select a document type and fill in the details</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* AI Features Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium">Gap Analysis Reports</h4>
              <p className="text-sm text-muted-foreground">
                AI-generated comprehensive gap analysis with recommendations and timelines
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <BookOpen className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="font-medium">Standard Operating Procedures</h4>
              <p className="text-sm text-muted-foreground">
                NABH-compliant SOPs with proper structure, responsibilities, and quality controls
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <GraduationCap className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium">Training Modules</h4>
              <p className="text-sm text-muted-foreground">
                Complete training outlines with assessments and learning objectives
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AIToolsPage;
